import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';

// ✅ NEW IMPORTS - Using shared API functions
import { signIn, signUp, sendPasswordResetEmail } from '../shared/api/auth';
import { supabase } from '../shared/supabase';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows 
} from '../shared/config/theme';

const LoginPage = ({ onAuthSuccess, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password Reset States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  // ✅ REFACTORED - Now uses shared API functions
  const handleLogin = async () => {
    setLoading(true);
    setLoginError('');

    try {
      if (isLogin) {
        // ✅ UPDATED - Sign In using shared API
        const { user, error } = await signIn(userEmail, password);

        if (error) throw error;

        console.log('✅ Signed in successfully');

        // Check for intended journey
        const intendedJourney = sessionStorage.getItem('intendedJourney');
        if (intendedJourney) {
          sessionStorage.setItem('selectedJourneyId', intendedJourney);
          sessionStorage.removeItem('intendedJourney');
        }

        onAuthSuccess();

      } else {
        // Sign Up - Validate name is provided
        if (!userName || userName.trim() === '') {
          throw new Error('Please enter your full name');
        }

        console.log('🚀 Starting signup process...');

        // ✅ UPDATED - Sign Up using shared API
        const { user, error } = await signUp(userEmail, password, userName.trim());

        if (error) throw error;
        
        if (!user) {
          throw new Error('Signup failed - no user created');
        }

        console.log('✅ User created:', user.id);
        console.log('📝 Creating profile with name:', userName);

        // Create profile with error handling
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: userEmail,
            full_name: userName.trim()
          })
          .select()
          .single();

        if (profileError) {
          console.error('❌ Profile creation error:', profileError);
          console.log('⚠️ Continuing without profile...');
        } else {
          console.log('✅ Profile created:', profileData);
        }

        console.log('📊 Creating user progress...');

        // Initialize user progress
        const { error: progressError } = await supabase
          .from('user_progress')
          .insert({
            user_id: user.id,
            total_sessions: 0,
            current_streak: 0,
            longest_streak: 0,
            mindful_minutes: 0
          });

        if (progressError) {
          console.error('⚠️ Progress creation error:', progressError);
        } else {
          console.log('✅ User progress created');
        }

        // Check for intended journey
        const intendedJourney = sessionStorage.getItem('intendedJourney');
        if (intendedJourney) {
          sessionStorage.setItem('selectedJourneyId', intendedJourney);
          sessionStorage.removeItem('intendedJourney');
        }

        console.log('🎉 Signup complete! Calling onAuthSuccess...');
        onAuthSuccess();
      }
    } catch (error) {
      console.error('❌ Auth error:', error);
      setLoginError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ REFACTORED - Now uses shared API function
  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      // ✅ UPDATED - Using shared API function
      const { success, error } = await sendPasswordResetEmail(resetEmail);

      if (error) throw error;

      setResetSuccess(true);
      console.log('✅ Password reset email sent to:', resetEmail);
    } catch (error) {
      console.error('❌ Password reset error:', error);
      setResetError(error.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
    setResetLoading(false);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background}`}>
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className={`${cardStyles.soft.background} backdrop-blur-lg ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-8 max-w-md w-full border border-peach-200 relative`}>
          {/* Close Button - Only show if onClose prop exists */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
          )}

          <div className="text-center mb-8">
            <img
              src={`${process.env.PUBLIC_URL}/manifest_logo.png`}
              alt="the Daily MANIFEST"
              style={{
                height: 'clamp(56px, 14vw, 88px)',
                width: 'auto',
                maxWidth: 220,
                objectFit: 'contain',
                margin: '0 auto 8px',
                display: 'block',
              }}
            />
            <p className="text-brown-600">Your daily mindset companion</p>
          </div>
          
          {/* ✅ Tab Switcher - Using theme colors */}
          <div className="flex bg-peach-100 rounded-xl p-1 mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-brown-600 hover:text-gray-900'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                !isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-brown-600 hover:text-gray-900'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* ✅ Error Message - Using theme colors */}
          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {loginError}
            </div>
          )}

          <div className="space-y-6">
            {!isLogin && (
              <div>
                <label className="block text-gray-900 text-sm font-medium mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 bg-white border border-peach-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-gray-900 text-sm font-medium mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 bg-white border border-peach-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-gray-900 text-sm font-medium mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? "Enter your password" : "Create a password (min 6 characters)"}
                className="w-full px-4 py-3 bg-white border border-peach-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                required
              />
            </div>

            {/* Forgot Password Link - Only show on Sign In */}
            {isLogin && (
              <div className="text-right">
                <button
                  onClick={() => setShowForgotPassword(true)}
                  className="text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            )}
            
            {/* ✅ Primary Button - Using theme button styles */}
            <button 
              onClick={handleLogin}
              disabled={loading}
              className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-3 px-6 disabled:opacity-50 disabled:transform-none`}
            >
              {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Forgot Password Modal - Using theme styles */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.soft.background} ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-8 max-w-md w-full border border-peach-200 relative`}>
            <button
              onClick={handleCloseForgotPassword}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {!resetSuccess ? (
              <>
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${gradients.button} rounded-full flex items-center justify-center mx-auto mb-4 ${shadows.md}`}>
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h2>
                  <p className="text-brown-600 text-sm">
                    Enter your email and we'll send you a link to reset your password
                  </p>
                </div>

                {resetError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    {resetError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-900 text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 bg-white border border-peach-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      onKeyDown={(e) => e.key === 'Enter' && handlePasswordReset()}
                    />
                  </div>

                  <button
                    onClick={handlePasswordReset}
                    disabled={resetLoading}
                    className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-3 px-6 disabled:opacity-50`}
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>

                  <button
                    onClick={handleCloseForgotPassword}
                    className="w-full text-brown-600 hover:text-gray-900 py-2 text-sm font-medium transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email!</h3>
                <p className="text-brown-700 mb-6">
                  We've sent a password reset link to <span className="font-semibold text-gray-900">{resetEmail}</span>
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 text-left">
                  <p className="text-gray-900 text-sm mb-2">
                    <strong>Next steps:</strong>
                  </p>
                  <ol className="text-brown-700 text-sm space-y-1 list-decimal list-inside">
                    <li>Check your email inbox</li>
                    <li>Click the reset link in the email</li>
                    <li>Create your new password</li>
                  </ol>
                </div>
                <button
                  onClick={handleCloseForgotPassword}
                  className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-3 px-6`}
                >
                  Got it!
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;